import { formatPlanTokens, zonedParts, type PlatformPeopleDto, type PlatformPersonDto } from '@dbrain/shared';
import { Alert, Badge, Button, Dialog, Field, Icons, Menu, MenuItem, MenuSeparator, Skeleton, TextArea, TextInput } from '@dbrain/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { DataTable, Pagination, SearchBox, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { licenseDay } from '@dbrain/app-core/license-days';
import { dayAfter, GRANT_DAYS, GRANT_ZONE, grantDayValid, lastDayOf, untilDay } from './plan-form.js';

/**
 * Platform › Kullanıcılar (Phase 16a, ADR-0023, 06 §28.4): the bireysel installation's people, their plan, today's
 * tokens and their documents. Pro by hand until a day and with a reason (D-269), Pro taken back, and the internal
 * network addresses of a person's own space (the kurumsal-era Firmalar switch, now per person).
 */
const USERS_KEY = ['platform', 'users'] as const;

const nameOf = (person: PlatformPersonDto) => person.displayName.trim() || person.email;
const thisYear = () => zonedParts(new Date(), GRANT_ZONE).year;

/** "Pro · 30 Kasım'a kadar" with the reason under it, "Pro · abonelik", or Ücretsiz */
function PlanCell({ person }: { person: PlatformPersonDto }) {
  const { t, i18n } = useTranslation();
  if (person.plan.id !== 'pro') return <span>{t('b2cPlatform.planNames.free')}</span>;
  // Phase 16c: a subscription runs on its own (renewed, cancelled or retried in Abonelikler ve ödemeler)
  if (person.plan.source === 'subscription') return <span data-testid="plan-subscription">{t('b2cBilling.users.subscription')}</span>;
  const until = person.plan.proUntil
    ? t('b2cPlatform.users.proUntil', { day: untilDay(lastDayOf(person.plan.proUntil), i18n.language, thisYear()) })
    : t('b2cPlatform.planNames.pro');
  return (
    <span className="person-cell">
      <span className="person-cell__name">{until}</span>
      {person.plan.reason ? (
        <span className="person-cell__email" title={person.plan.reason}>
          {person.plan.reason}
        </span>
      ) : null}
    </span>
  );
}

export function UsersTab() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const zone = useCompanyZone();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const query = new URLSearchParams({ q, offset: String((page - 1) * size), limit: String(size) }).toString();
  const people = useQuery({ queryKey: [...USERS_KEY, query], queryFn: () => api<PlatformPeopleDto>('GET', `/api/platform/users?${query}`), placeholderData: keepPreviousData });
  const [granting, setGranting] = useState<PlatformPersonDto | null>(null);
  const [revoking, setRevoking] = useState<PlatformPersonDto | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: USERS_KEY });
  const internal = useMutation({
    mutationFn: ({ person, allowed }: { person: PlatformPersonDto; allowed: boolean }) => api('PUT', `/api/platform/tenants/${person.tenantId}/internal-providers`, { allowed }),
    onMutate: () => setDone(null),
    onSuccess: (_, { person, allowed }) => setDone(t(allowed ? 'b2cPlatform.users.internalDone.on' : 'b2cPlatform.users.internalDone.off', { name: nameOf(person) })),
    onSettled: refresh,
  });
  const finished = (message: string) => {
    setGranting(null);
    setRevoking(null);
    setDone(message);
    refresh();
  };
  const data = people.data;

  const columns: Column<PlatformPersonDto>[] = [
    {
      id: 'person',
      header: t('b2cPlatform.users.columns.person'),
      cell: (p) => (
        <span className="person-cell">
          <span className="user-cell__name">
            <span className="person-cell__name">{nameOf(p)}</span>
            {!p.verified ? <Badge tone="warning">{t('b2cPlatform.users.unverified')}</Badge> : null}
            {p.allowInternalProviderUrls ? <Badge tone="info">{t('b2cPlatform.users.internal')}</Badge> : null}
          </span>
          <span className="person-cell__email">{p.email}</span>
        </span>
      ),
    },
    { id: 'plan', header: t('b2cPlatform.users.columns.plan'), width: 'lg', cell: (p) => <PlanCell person={p} /> },
    {
      id: 'createdAt',
      header: t('b2cPlatform.users.columns.createdAt'),
      width: 'sm',
      cell: (p) => <time dateTime={p.createdAt}>{new Date(p.createdAt).toLocaleDateString(locale, { dateStyle: 'medium', timeZone: zone })}</time>,
    },
    { id: 'today', header: t('b2cPlatform.users.columns.today'), width: 'sm', numeric: true, cell: (p) => formatPlanTokens(p.tokensToday, locale) },
    { id: 'documents', header: t('b2cPlatform.users.columns.documents'), width: 'xs', numeric: true, cell: (p) => p.documents.toLocaleString(locale) },
  ];

  const rowMenu = (p: PlatformPersonDto) => (
    <Menu
      trigger={<Icons.more size={18} />}
      label={t('b2cPlatform.users.menu.label', { name: nameOf(p) })}
      align="right"
      portal
      triggerClassName="dt-row-menu"
      data-testid={`user-actions-${p.id}`}
    >
      <MenuItem icon={<Icons.gift size={16} />} onClick={() => setGranting(p)} data-testid="user-grant-pro">
        {t('b2cPlatform.users.menu.grant')}
      </MenuItem>
      {p.plan.id === 'pro' ? (
        <MenuItem icon={<Icons.undo size={16} />} danger onClick={() => setRevoking(p)} data-testid="user-revoke-pro">
          {t('b2cPlatform.users.menu.revoke')}
        </MenuItem>
      ) : null}
      {p.tenantId ? (
        <>
          <MenuSeparator />
          <MenuItem icon={<Icons.lock size={16} />} onClick={() => internal.mutate({ person: p, allowed: !p.allowInternalProviderUrls })} data-testid="user-internal">
            {t(p.allowInternalProviderUrls ? 'b2cPlatform.users.menu.blockInternal' : 'b2cPlatform.users.menu.allowInternal')}
          </MenuItem>
        </>
      ) : null}
    </Menu>
  );

  return (
    <div className="stack b2c-tab">
      <div className="dt-toolbar">
        <SearchBox
          value={q}
          onChange={(value) => {
            setQ(value);
            setPage(1);
          }}
          placeholder={t('b2cPlatform.users.search')}
          testId="users-search"
        />
      </div>
      {done ? <Alert tone="success" title={done} data-testid="users-done" /> : null}
      <ErrorAlert error={people.error ?? internal.error} />
      {!data && people.isPending ? <Skeleton block /> : null}
      {data ? (
        <DataTable
          label={t('b2cPlatform.users.label')}
          columns={columns}
          rows={data.rows}
          rowKey={(p) => p.id}
          rowMenu={rowMenu}
          rowTestId={(p) => `user-row-${p.id}`}
          loading={people.isFetching}
          empty={q ? t('b2cPlatform.users.noMatch') : t('b2cPlatform.users.empty')}
          data-testid="users-table"
          footer={
            <Pagination
              page={page}
              size={size}
              total={data.total}
              noun={t('b2cPlatform.users.noun')}
              onPage={setPage}
              onSize={(value) => {
                setSize(value);
                setPage(1);
              }}
            />
          }
        />
      ) : null}
      {granting ? <GrantDialog person={granting} onClose={() => setGranting(null)} onDone={finished} /> : null}
      {revoking ? <RevokeDialog person={revoking} onClose={() => setRevoking(null)} onDone={finished} /> : null}
    </div>
  );
}

function ReasonField({ value, onChange, invalid, testId }: { value: string; onChange: (value: string) => void; invalid: boolean; testId: string }) {
  const { t } = useTranslation();
  return (
    <Field label={t('b2cPlatform.users.reason')} hint={t('b2cPlatform.users.reasonHint')} error={invalid ? t('b2cPlatform.users.reasonShort') : undefined}>
      {({ id, describedBy, invalid: shown }) => (
        <TextArea
          id={id}
          value={value}
          rows={3}
          maxLength={500}
          required
          aria-describedby={describedBy}
          aria-invalid={shown || undefined}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      )}
    </Field>
  );
}

/** "Pro ver": the reason and the last day (D-269); a new grant takes the place of the one running */
function GrantDialog({ person, onClose, onDone }: { person: PlatformPersonDto; onClose: () => void; onDone: (message: string) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const formId = useId();
  const [now] = useState(() => new Date());
  const first = dayAfter(now, 0);
  const last = dayAfter(now, GRANT_DAYS.last);
  const year = Number(first.slice(0, 4));
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState(() => dayAfter(now, GRANT_DAYS.default));
  const [tried, setTried] = useState(false);
  const reasonOk = reason.trim().length >= 3;
  const untilOk = grantDayValid(until, now);
  const grant = useMutation({
    mutationFn: () => api('POST', `/api/platform/users/${person.id}/pro`, { reason: reason.trim(), until }),
    onSuccess: () => onDone(t('b2cPlatform.users.grant.done', { name: nameOf(person), day: untilDay(until, locale, year) })),
  });
  const lastText = licenseDay(last, locale);
  return (
    <Dialog
      title={t('b2cPlatform.users.grant.title')}
      onClose={onClose}
      closeLabel={t('common.close')}
      data-testid="grant-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} icon={<Icons.gift size={16} />} busy={grant.isPending} data-testid="grant-submit">
            {t('b2cPlatform.users.grant.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="dialog-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (reasonOk && untilOk) grant.mutate();
        }}
      >
        <p className="b2c-dialog__who">
          <strong>{nameOf(person)}</strong> · {person.email}
        </p>
        <p className="muted">{t('b2cPlatform.users.grant.body')}</p>
        {person.plan.id === 'pro' && person.plan.proUntil ? (
          <p className="muted">{t('b2cPlatform.users.grant.replaces', { day: untilDay(lastDayOf(person.plan.proUntil), locale, year) })}</p>
        ) : null}
        <ReasonField value={reason} onChange={setReason} invalid={tried && !reasonOk} testId="grant-reason" />
        <Field
          label={t('b2cPlatform.users.grant.until')}
          hint={t('b2cPlatform.users.grant.untilHint', { last: lastText })}
          error={tried && !untilOk ? t('b2cPlatform.users.grant.untilInvalid', { last: lastText }) : undefined}
        >
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              value={until}
              min={first}
              max={last}
              required
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(e) => setUntil(e.target.value)}
              data-testid="grant-until"
            />
          )}
        </Field>
        <ErrorAlert error={grant.error} />
      </form>
    </Dialog>
  );
}

/** "Pro'yu geri al": now, with a reason; the plan job sends "Pro bitti" */
function RevokeDialog({ person, onClose, onDone }: { person: PlatformPersonDto; onClose: () => void; onDone: (message: string) => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const reasonOk = reason.trim().length >= 3;
  const revoke = useMutation({
    mutationFn: () => api('DELETE', `/api/platform/users/${person.id}/pro`, { reason: reason.trim() }),
    onSuccess: () => onDone(t('b2cPlatform.users.revoke.done', { name: nameOf(person) })),
  });
  return (
    <Dialog
      title={t('b2cPlatform.users.revoke.title')}
      onClose={onClose}
      closeLabel={t('common.close')}
      data-testid="revoke-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} variant="danger" busy={revoke.isPending} data-testid="revoke-submit">
            {t('b2cPlatform.users.revoke.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="dialog-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (reasonOk) revoke.mutate();
        }}
      >
        <p className="b2c-dialog__who">
          <strong>{nameOf(person)}</strong> · {person.email}
        </p>
        <p className="muted">{t('b2cPlatform.users.revoke.body')}</p>
        <ReasonField value={reason} onChange={setReason} invalid={tried && !reasonOk} testId="revoke-reason" />
        <ErrorAlert error={revoke.error} />
      </form>
    </Dialog>
  );
}
